import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search, X, Package, Plus, Upload, Check, Globe,
  ChevronLeft, ChevronRight, FlaskConical,
} from 'lucide-react';
import { PageHeader, StatusBadge, EmptyState, DataTableSkeleton } from '../components/ui';
import { useToast, ToastContainer } from '../components/ui';
import { api } from '../api';

type VendorProductStatus = 'draft' | 'pending_review' | 'approved' | 'active' | 'paused' | 'archived';

interface AdminProduct {
  id: string;
  vendor_id: number;
  title: string;
  category: string;
  price_min: number | null;
  price_max: number | null;
  status: VendorProductStatus;
  is_test: boolean;
  shopify_product_id: string | null;
  shopify_status: string | null;
  published_storefront_ids: number[];
  vendor_name: string;
  vendor_slug: string | null;
  submitted_at: string | null;
  updated_at: string;
}

interface VendorOption {
  id: number;
  name: string;
}

const STATUS_TABS: (VendorProductStatus | '')[] = ['', 'pending_review', 'approved', 'active', 'draft', 'paused', 'archived'];

const statusVariantMap: Record<VendorProductStatus, 'success' | 'warning' | 'error' | 'info' | 'neutral'> = {
  draft: 'neutral',
  pending_review: 'warning',
  approved: 'info',
  active: 'success',
  paused: 'warning',
  archived: 'neutral',
};

const CATEGORIES = ['general', 'apparel', 'accessories', 'beauty', 'home', 'electronics', 'food', 'sports', 'toys', 'other'];

const PAGE_SIZE = 50;

export default function AdminProducts() {
  const { t } = useTranslation(['adminProducts', 'products']);
  const { toasts, addToast, removeToast } = useToast();
  const navigate = useNavigate();

  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);

  const [statusFilter, setStatusFilter] = useState<string>('');
  const [vendorFilter, setVendorFilter] = useState<number | undefined>(undefined);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showTestOnly, setShowTestOnly] = useState(false);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [vendors, setVendors] = useState<VendorOption[]>([]);

  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectNotes, setRejectNotes] = useState('');
  const [publishId, setPublishId] = useState<string | null>(null);
  const [publishInput, setPublishInput] = useState('');

  useEffect(() => {
    api.getVendors().then((r: any) => setVendors((r.vendors || r.data || []).map((v: any) => ({ id: v.id, name: v.name })))).catch(() => {});
  }, []);

  const handleSearchChange = (val: string) => {
    setSearch(val);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setDebouncedSearch(val);
      setOffset(0);
    }, 350);
  };

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.getAdminVendorProducts({
        status: statusFilter || undefined,
        vendor_id: vendorFilter,
        is_test: showTestOnly ? true : undefined,
        search: debouncedSearch || undefined,
        category: categoryFilter || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setProducts(result.data || []);
      setTotal(result.total ?? (result.data || []).length);
    } catch {
      addToast('Failed to load products', 'error');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, vendorFilter, showTestOnly, debouncedSearch, categoryFilter, offset]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleApprove = async (id: string) => {
    try {
      const result = await api.approveVendorProduct(id);
      if (result.shopify_error) addToast(`Approved but Shopify push failed`, 'warning');
      else addToast('Product approved', 'success');
      fetchProducts();
    } catch { addToast('Failed to approve', 'error'); }
  };

  const handleReject = async () => {
    if (!rejectId) return;
    try {
      await api.rejectVendorProduct(rejectId, rejectNotes);
      addToast('Product rejected', 'success');
      setRejectId(null);
      setRejectNotes('');
      fetchProducts();
    } catch { addToast('Failed to reject', 'error'); }
  };

  const handlePublish = async () => {
    if (!publishId || !publishInput.trim()) return;
    const ids = publishInput.split(',').map(s => s.trim()).filter(Boolean);
    try {
      await api.publishVendorProduct(publishId, ids);
      addToast('Published to storefronts', 'success');
      setPublishId(null);
      setPublishInput('');
      fetchProducts();
    } catch { addToast('Failed to publish', 'error'); }
  };

  const handleUnpublish = async (id: string) => {
    const product = products.find(p => p.id === id);
    if (!product?.published_storefront_ids?.length) return;
    try {
      await api.unpublishVendorProduct(id, product.published_storefront_ids.map(String));
      addToast('Unpublished', 'success');
      fetchProducts();
    } catch { addToast('Failed to unpublish', 'error'); }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  const fmtPrice = (min: number | null, max: number | null) => {
    if (min == null) return '—';
    if (max == null || max === min) return `$${min}`;
    return `$${min} – $${max}`;
  };

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <PageHeader
          title={t('title')}
          subtitle={t('subtitle', { count: total })}
        />
        <div className="flex items-center gap-2">
          <Link to="/admin/products/import-csv" className="btn-secondary flex items-center gap-1.5 text-sm">
            <Upload className="w-3.5 h-3.5" /> {t('importCsv')}
          </Link>
          <Link to="/admin/products/new" className="btn-primary flex items-center gap-1.5 text-sm">
            <Plus className="w-3.5 h-3.5" /> {t('addProduct')}
          </Link>
        </div>
      </div>

      {/* Search + filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30 pointer-events-none" />
          <input
            value={search}
            onChange={e => handleSearchChange(e.target.value)}
            placeholder={t('searchPlaceholder')}
            className="w-full pl-8 pr-8 py-2 text-sm text-white/80 placeholder:text-white/25 bg-transparent outline-none"
            style={{ border: '1px solid rgba(255,255,255,0.10)', borderRadius: '2px', backgroundColor: 'rgba(255,255,255,0.03)' }}
          />
          {search && (
            <button onClick={() => { setSearch(''); setDebouncedSearch(''); setOffset(0); }}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <select
          value={vendorFilter ?? ''}
          onChange={e => { setVendorFilter(e.target.value ? parseInt(e.target.value) : undefined); setOffset(0); }}
          className="text-xs px-3 py-2 text-white/60 bg-transparent outline-none cursor-pointer"
          style={{ border: '1px solid rgba(255,255,255,0.10)', borderRadius: '2px', backgroundColor: 'rgba(255,255,255,0.03)' }}
        >
          <option value="">{t('allVendors')}</option>
          {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>

        <select
          value={categoryFilter}
          onChange={e => { setCategoryFilter(e.target.value); setOffset(0); }}
          className="text-xs px-3 py-2 text-white/60 bg-transparent outline-none cursor-pointer"
          style={{ border: '1px solid rgba(255,255,255,0.10)', borderRadius: '2px', backgroundColor: 'rgba(255,255,255,0.03)' }}
        >
          <option value="">{t('allCategories')}</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{t(`products:categories.${c}`)}</option>)}
        </select>

        <label className="flex items-center gap-1.5 text-xs text-white/40 cursor-pointer ml-auto">
          <input type="checkbox" checked={showTestOnly} onChange={e => { setShowTestOnly(e.target.checked); setOffset(0); }} className="accent-[#FFD400]" />
          <FlaskConical className="w-3 h-3" />
          {t('testOnly')}
        </label>
      </div>

      {/* Status tabs */}
      <div className="flex gap-1.5 flex-wrap">
        {STATUS_TABS.map(s => (
          <button
            key={s || '_all'}
            onClick={() => { setStatusFilter(s); setOffset(0); }}
            className="text-xs px-3 py-1.5 transition-colors"
            style={{
              borderRadius: '2px',
              border: statusFilter === s ? '1px solid rgba(255,212,0,0.35)' : '1px solid rgba(255,255,255,0.08)',
              backgroundColor: statusFilter === s ? 'rgba(255,212,0,0.08)' : 'rgba(255,255,255,0.03)',
              color: statusFilter === s ? '#FFD400' : 'rgba(255,255,255,0.50)',
            }}
          >
            {s ? t(`products:status.${s}`) : t('allStatuses')}
          </button>
        ))}
      </div>

      {/* Product table */}
      {loading ? (
        <DataTableSkeleton rows={8} cols={7} />
      ) : products.length === 0 ? (
        <EmptyState
          icon={<Package className="w-8 h-8" />}
          message={t('noProducts')}
          action={
            (debouncedSearch || statusFilter || vendorFilter || categoryFilter)
              ? <button onClick={() => { setSearch(''); setDebouncedSearch(''); setStatusFilter(''); setVendorFilter(undefined); setCategoryFilter(''); }} className="btn-secondary text-sm">{t('clearFilters')}</button>
              : undefined
          }
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <colgroup>
              <col style={{ width: '30%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '12%' }} />
            </colgroup>
            <thead>
              <tr className="border-b border-white/[0.06]">
                <th className="text-left px-4 py-3 text-xs text-white/40 uppercase">{t('columns.product')}</th>
                <th className="text-left px-4 py-3 text-xs text-white/40 uppercase">{t('columns.vendor')}</th>
                <th className="text-left px-4 py-3 text-xs text-white/40 uppercase">{t('columns.price')}</th>
                <th className="text-left px-4 py-3 text-xs text-white/40 uppercase">{t('columns.status')}</th>
                <th className="text-left px-4 py-3 text-xs text-white/40 uppercase">{t('columns.shopify')}</th>
                <th className="text-left px-4 py-3 text-xs text-white/40 uppercase">{t('columns.storefronts')}</th>
                <th className="text-left px-4 py-3 text-xs text-white/40 uppercase">{t('columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {products.map(p => (
                <tr
                  key={p.id}
                  className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors cursor-pointer"
                  onClick={() => navigate(`/admin/products/${p.id}`)}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-white/80 line-clamp-1">{p.title}</span>
                      {p.is_test && (
                        <span className="text-[10px] px-1.5 py-0.5 text-white/40 bg-white/5 rounded shrink-0" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>TEST</span>
                      )}
                    </div>
                    <span className="text-xs text-white/30">{t(`products:categories.${p.category}`)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-white/60 text-xs">{p.vendor_name}</div>
                  </td>
                  <td className="px-4 py-3 text-white/50 text-xs">{fmtPrice(p.price_min, p.price_max)}</td>
                  <td className="px-4 py-3">
                    <StatusBadge variant={statusVariantMap[p.status]}>{t(`products:status.${p.status}`)}</StatusBadge>
                  </td>
                  <td className="px-4 py-3">
                    {p.shopify_product_id ? (
                      <span className="text-[10px] px-1.5 py-0.5 text-[#4ade80] bg-[#4ade80]/10 rounded">{t('shopifySynced')}</span>
                    ) : (
                      <span className="text-xs text-white/20">{t('shopifyNotSynced')}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {p.published_storefront_ids?.length > 0 ? (
                      <div className="flex items-center gap-1">
                        <Globe className="w-3 h-3 text-[#4ade80]" />
                        <span className="text-xs text-white/50">{p.published_storefront_ids.length}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-white/15">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      {p.status === 'pending_review' && (
                        <>
                          <button onClick={() => handleApprove(p.id)} className="p-1.5 text-[#4ade80] hover:bg-[#4ade80]/10 rounded transition-colors" title={t('detail.approve')}>
                            <Check className="w-4 h-4" />
                          </button>
                          <button onClick={() => setRejectId(p.id)} className="p-1.5 text-[#f87171] hover:bg-[#f87171]/10 rounded transition-colors" title={t('detail.reject')}>
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {p.shopify_product_id && !p.published_storefront_ids?.length && (
                        <button onClick={() => setPublishId(p.id)} className="p-1.5 text-[#a78bfa] hover:bg-[#a78bfa]/10 rounded transition-colors" title={t('detail.publish')}>
                          <Globe className="w-4 h-4" />
                        </button>
                      )}
                      {(p.published_storefront_ids?.length ?? 0) > 0 && (
                        <button onClick={() => handleUnpublish(p.id)} className="p-1.5 text-white/30 hover:text-[#f87171] hover:bg-[#f87171]/10 rounded transition-colors" title={t('detail.unpublish')}>
                          <Globe className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-white/30">
            {t('page', { current: currentPage, total: totalPages })} · {t('showing', { count: products.length, total })}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={offset === 0}
              className="flex items-center gap-1 px-3 py-1.5 text-xs disabled:opacity-30 transition-colors"
              style={{ border: '1px solid rgba(255,255,255,0.10)', borderRadius: '2px', color: 'rgba(255,255,255,0.55)' }}
            >
              <ChevronLeft className="w-3.5 h-3.5" /> {t('prev')}
            </button>
            <button
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={offset + PAGE_SIZE >= total}
              className="flex items-center gap-1 px-3 py-1.5 text-xs disabled:opacity-30 transition-colors"
              style={{ border: '1px solid rgba(255,255,255,0.10)', borderRadius: '2px', color: 'rgba(255,255,255,0.55)' }}
            >
              {t('next')} <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-ink-950/80 backdrop-blur-sm" onClick={() => setRejectId(null)} />
          <div className="card p-6 w-full max-w-md relative z-10">
            <h3 className="text-lg font-display font-bold text-white mb-4">{t('detail.reject')}</h3>
            <textarea
              value={rejectNotes}
              onChange={e => setRejectNotes(e.target.value)}
              className="input w-full min-h-[80px] mb-4"
              placeholder={t('detail.rejectionPlaceholder')}
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setRejectId(null)} className="btn-ghost">{t('csv.cancel')}</button>
              <button onClick={handleReject} className="btn-primary bg-[#f87171] hover:bg-[#f87171]/80">{t('detail.reject')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Publish Modal */}
      {publishId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-ink-950/80 backdrop-blur-sm" onClick={() => setPublishId(null)} />
          <div className="card p-6 w-full max-w-md relative z-10">
            <h3 className="text-lg font-display font-bold text-white mb-4">{t('detail.publishToStorefronts')}</h3>
            <p className="text-sm text-white/50 mb-3">{t('detail.publishHint')}</p>
            <input
              value={publishInput}
              onChange={e => setPublishInput(e.target.value)}
              className="input w-full mb-4"
              placeholder="12345, 67890"
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setPublishId(null)} className="btn-ghost">{t('csv.cancel')}</button>
              <button onClick={handlePublish} className="btn-primary">{t('detail.publish')}</button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}
